import {
  formatPropertyType,
  formatShortenedPropertyType,
  getSiteGlossaryEntries,
} from '@aloft/toolkit-shared';

import type { SalesComparisonGridColumnData, SalesComparisonRows } from '../types.ts';
import { getSalesComparisonNestedValue } from './getSalesComparisonNestedValue.ts';

type GlossaryDef = {
  sort: number;
  line: string;
  matches: (v: string) => boolean;
};

const word = (s: string) => {
  const esc = s.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
  return new RegExp(String.raw`\b${esc}\b`, 'i');
};

const PROPERTY_TYPE_KEYS = [
  'singleFamilyResidence',
  'multiFamily',
  'manufacturedHome',
  'condominium',
  'townhouse',
] as const;

const PROPERTY_TYPE_GLOSSARY: GlossaryDef[] = PROPERTY_TYPE_KEYS.flatMap((propertyType) => {
  const abbrev = formatShortenedPropertyType(propertyType);
  const full = formatPropertyType(propertyType);
  if (!abbrev || !full || abbrev === full) return [];
  return [
    {
      sort: 50,
      line: `${abbrev} = ${full}`,
      matches: (v: string) => word(abbrev).test(v),
    },
  ];
});

const siteGlossaryDefs = (): GlossaryDef[] =>
  getSiteGlossaryEntries().map((entry, index) => ({
    sort: 200 + index,
    line: `${entry.abbrev} = ${entry.full}`,
    matches: (v: string) => word(entry.abbrev).test(v),
  }));

const GLOSSARY_DEFS: GlossaryDef[] = [
  { sort: 10, line: 'B = Beneficial', matches: (v) => v.startsWith('B;') },
  { sort: 11, line: 'N = Neutral', matches: (v) => v.startsWith('N;') },
  { sort: 12, line: 'A = Adverse', matches: (v) => v.startsWith('A;') },

  ...PROPERTY_TYPE_GLOSSARY,

  { sort: 60, line: 'AG = Attached Garage', matches: (v) => /\d+\s+AG\b/.test(v) },
  { sort: 61, line: 'DG = Detached Garage', matches: (v) => /\d+\s+DG\b/.test(v) },
  {
    sort: 62,
    line: 'C = Carport',
    matches: (v) => v.split(';').some((part) => /^\d+\s+C$/.test(part.trim())),
  },

  { sort: 70, line: 'DK = Deck', matches: (v) => /\bDK\b/.test(v) },
  { sort: 71, line: 'PAT = Patio', matches: (v) => /\bPAT\b/.test(v) },
  { sort: 72, line: 'PCH = Porch', matches: (v) => /\bPCH\b/.test(v) },

  ...siteGlossaryDefs(),
];

export const buildSalesComparisonGlossaryText = (
  filteredRows: SalesComparisonRows,
  properties: SalesComparisonGridColumnData[],
): string | null => {
  const values = filteredRows.flatMap((row) =>
    row.key.flatMap((fieldPath) =>
      properties.flatMap((p) => {
        const cell = getSalesComparisonNestedValue(p, fieldPath);
        return cell ? [cell] : [];
      }),
    ),
  );

  const entries: { sort: number; line: string }[] = [];
  const seen = new Set<string>();

  GLOSSARY_DEFS.forEach((def) => {
    const hit = values.some((v) => def.matches(v));
    if (hit && !seen.has(def.line)) {
      seen.add(def.line);
      entries.push({ sort: def.sort, line: def.line });
    }
  });

  if (entries.length === 0) return null;

  entries.sort((a, b) => a.sort - b.sort);
  return entries.map((e) => e.line).join(' | ');
};
